import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { startBot, stopBot, getBotStatus, restartBot } from "../api-services/botManagement";
import { setAmount, getLatestAmount } from "../api-services/setUsetAmount";
import { setMartingale, getLatestMartingale } from "../api-services/matingaleManagement";
import { setTarget, getLatestTarget } from "../api-services/targetMnagement";
import { setTrendTracker, getLatestTrendTracker } from "../api-services/trendTracker";
import { getDailyStats } from "../api-services/dailyStats";
import { getCooldownStatus, getCooldownSettings, setCooldownSettings } from "../api-services/cooldownManagement";

export const useDashboard = () => {
    const navigate = useNavigate();
    const [botStatus, setBotStatus] = useState('Unknown');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [statusLoading, setStatusLoading] = useState(true);
    
    const [currentAmount, setCurrentAmount] = useState(null);
    const [currentMartingale, setCurrentMartingale] = useState(null);
    const [currentTarget, setCurrentTarget] = useState(null);
    const [currentTrendTracker, setCurrentTrendTracker] = useState(null);
    const [dailyStats, setDailyStats] = useState({
        target: null,
        profit: null,
        remaining: null,
        targetReached: false,
        canChangeTarget: true
    });
    const [cooldownStatus, setCooldownStatus] = useState({
        inCooldown: false,
        remainingMinutes: 0,
        lastWinTime: null,
        cooldownMinutes: null,
        message: ''
    });
    const [cooldownSettings, setCooldownSettingsState] = useState({
        cooldownMinutes: null
    });

    const userEmail = localStorage.getItem('userEmail') || 'User';
    const userRole = localStorage.getItem('userRole') || '';

    useEffect(() => {
        fetchStatus();
        fetchCurrentAmount();
        fetchCurrentMartingale();
        fetchCurrentTarget();
        fetchCurrentTrendTracker();
        fetchDailyStats();
        fetchCooldownStatus();
        fetchCooldownSettings();
        const interval = setInterval(() => {
            fetchStatus();
            fetchDailyStats();
            fetchCooldownStatus();
        }, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userRole');
        navigate('/');
    };

    const fetchStatus = async () => {
        try {
            const response = await getBotStatus();
            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    const status = data.isCapturing ? 'Running' : 'Stopped';
                    setBotStatus(data.message || status);
                } else {
                    setBotStatus('Unknown');
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching status:', error);
        } finally {
            setStatusLoading(false);
        }
    };

    const fetchCurrentAmount = async () => {
        try {
            const response = await getLatestAmount();
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setCurrentAmount(parseFloat(data.data.amount));
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching amount:', error);
        }
    };

    const fetchCurrentMartingale = async () => {
        try {
            const response = await getLatestMartingale();
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setCurrentMartingale(parseInt(data.data.max_attempts));
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching martingale:', error);
        }
    };

    const fetchCurrentTarget = async () => {
        try {
            const response = await getLatestTarget();
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setCurrentTarget(parseFloat(data.data.target));
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching target:', error);
        }
    };

    const fetchCurrentTrendTracker = async () => {
        try {
            const response = await getLatestTrendTracker();
            if (response.ok) {
                const data = await response.json();
                if (data.number_of_directions !== undefined) {
                    setCurrentTrendTracker(parseInt(data.number_of_directions));
                } else if (data.success && data.data && data.data.number_of_directions !== undefined) {
                    setCurrentTrendTracker(parseInt(data.data.number_of_directions));
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching trend tracker:', error);
        }
    };

    const fetchDailyStats = async () => {
        try {
            const response = await getDailyStats();
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setDailyStats({
                        target: data.data.target,
                        profit: data.data.profit,
                        remaining: data.data.remaining,
                        targetReached: data.data.targetReached,
                        canChangeTarget: data.data.canChangeTarget
                    });
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching daily stats:', error);
        }
    };

    const fetchCooldownStatus = async () => {
        try {
            const response = await getCooldownStatus();
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setCooldownStatus({
                        inCooldown: data.data.inCooldown,
                        remainingMinutes: data.data.remainingMinutes,
                        lastWinTime: data.data.lastWinTime,
                        cooldownMinutes: data.data.cooldownMinutes,
                        message: data.data.message || ''
                    });
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching cooldown status:', error);
        }
    };

    const fetchCooldownSettings = async () => {
        try {
            const response = await getCooldownSettings();
            if (response.ok) {
                const data = await response.json();
                if (data.success && data.data) {
                    setCooldownSettingsState({
                        cooldownMinutes: data.data.cooldownMinutes
                    });
                }
            } else if (response.status === 401) {
                handleLogout();
            }
        } catch (error) {
            console.error('Error fetching cooldown settings:', error);
        }
    };

    const handleStart = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await startBot();
            if (response.ok) {
                setSuccess('Bot started successfully');
                fetchStatus();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to start bot');
            }
        } catch (error) {
            setError('Error starting bot');
        } finally {
            setLoading(false);
        }
    };

    const handleStop = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await stopBot();
            if (response.ok) {
                setSuccess('Bot stopped successfully');
                fetchStatus();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to stop bot');
            }
        } catch (error) {
            setError('Error stopping bot');
        } finally {
            setLoading(false);
        }
    };

    const handleRestart = async () => {
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await restartBot();
            if (response.ok) {
                setSuccess('Bot restarted successfully');
                fetchStatus();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to restart bot');
            }
        } catch (error) {
            setError('Error restarting bot');
        } finally {
            setLoading(false);
        }
    };

    const handleSetAmount = async (amount) => {
        if (!amount || isNaN(amount)) {
            setError('Please enter a valid amount');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await setAmount(parseFloat(amount));
            if (response.ok) {
                setSuccess('Amount set successfully');
                fetchCurrentAmount();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to set amount');
            }
        } catch (error) {
            setError('Error setting amount');
        } finally {
            setLoading(false);
        }
    };

    const handleSetMartingale = async (martingale) => {
        if (!martingale || isNaN(martingale) || parseInt(martingale) < 1) {
            setError('Please enter a valid number of attempts (minimum 1)');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await setMartingale(parseInt(martingale));
            if (response.ok) {
                setSuccess('Martingale max attempts set successfully');
                fetchCurrentMartingale();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to set martingale');
            }
        } catch (error) {
            setError('Error setting martingale');
        } finally {
            setLoading(false);
        }
    };

    const handleSetTarget = async (target) => {
        if (!target || isNaN(target)) {
            setError('Please enter a valid target amount');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await setTarget(parseFloat(target));
            if (response.ok) {
                setSuccess('Target set successfully');
                fetchCurrentTarget();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to set target');
            }
        } catch (error) {
            setError('Error setting target');
        } finally {
            setLoading(false);
        }
    };

    const handleSetTrendTracker = async (trendTracker) => {
        if (!trendTracker || isNaN(trendTracker) || parseInt(trendTracker) < 1) {
            setError('Please enter a valid number of directions (minimum 1)');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await setTrendTracker(parseInt(trendTracker));
            if (response.ok) {
                setSuccess('Trend tracker set successfully');
                fetchCurrentTrendTracker();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to set trend tracker');
            }
        } catch (error) {
            setError('Error setting trend tracker');
        } finally {
            setLoading(false);
        }
    };

    const handleSetCooldown = async (cooldownMinutes) => {
        if (!cooldownMinutes || isNaN(cooldownMinutes) || parseInt(cooldownMinutes) < 1) {
            setError('Please enter a valid cooldown duration (minimum 1 minute)');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await setCooldownSettings(parseInt(cooldownMinutes));
            if (response.ok) {
                setSuccess('Cooldown settings updated successfully');
                fetchCooldownSettings();
                fetchCooldownStatus();
            } else {
                const data = await response.json();
                setError(data.message || 'Failed to set cooldown settings');
            }
        } catch (error) {
            setError('Error setting cooldown settings');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = () => {
        const status = botStatus.toLowerCase();
        if (status.includes('running') || status.includes('active') || status.includes('capturing')) return '#22c55e';
        if (status.includes('stopped') || status.includes('not running')) return '#ef4444';
        return '#f59e0b';
    };

    return {
        botStatus,
        loading,
        error,
        success,
        statusLoading,
        currentAmount,
        currentMartingale,
        currentTarget,
        currentTrendTracker,
        dailyStats,
        cooldownStatus,
        cooldownSettings,
        userEmail,
        userRole,
        handleStart,
        handleStop,
        handleRestart,
        handleSetAmount,
        handleSetMartingale,
        handleSetTarget,
        handleSetTrendTracker,
        handleSetCooldown,
        handleLogout,
        getStatusColor
    };
};
